import { Box, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { COACH_MARK, FONT_SIZE, INK, MOTION, NEUTRAL } from "@/theme/tokens";

type Props = {
	/** Already-translated label ("Who we're asking"). Decorative: the control's own accessible name must say the same. */
	label: string;
	/** Whether the mark shows; the caller owns when it's done (usually `useOneTimeNotice`). */
	open: boolean;
	/** The control being introduced. Give it `coachMarkTargetSx` while `open` so it wears the ring. */
	children: ReactNode;
	/** `end` pins the bubble to the control's inline-end edge, for a control at the end of a row where a centred bubble would clip. */
	// DEFAULT: `center`, the bubble centred over the control.
	align?: CoachMarkAlign;
	dataTestId?: string;
};

export type CoachMarkAlign = "center" | "end";

const BUBBLE_ALIGN_SX = {
	center: { left: "50%", transform: "translateX(-50%)" },
	end: { insetInlineEnd: 0 },
} as const;

/** The introduced control's highlight: a brand wash and ring, drawn with `box-shadow` so the row never shifts. */
export const coachMarkTargetSx = (theme: Theme) => ({
	backgroundColor: alpha(theme.palette.primary.main, COACH_MARK.TARGET_WASH_ALPHA),
	boxShadow: `0 0 0 ${COACH_MARK.TARGET_RING_PX}px ${theme.palette.primary.main}`,
});

/**
 * A **coach mark**: a small dark label pinned above a control that's new, with an arrow pointing at it, until the
 * user has used it once. Never takes pointer events, so the control under it stays clickable. For a note with a
 * dismiss button use `OneTimeNotice` / `OneTimeNoticePopover`.
 */
const ENTER_SX = {
	"@keyframes coachMarkEnter": { from: { opacity: 0 }, to: { opacity: 1 } },
	animation: `coachMarkEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT} both`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

export const CoachMark: FC<Props> = ({ label, open, children, align = "center", dataTestId }) => (
	<Box sx={{ position: "relative", display: "inline-flex" }}>
		{open && (
			<Box
				component="span"
				aria-hidden
				data-testid={dataTestId}
				sx={{
					...ENTER_SX,
					...BUBBLE_ALIGN_SX[align],
					position: "absolute",
					bottom: `calc(100% + ${COACH_MARK.OFFSET_PX}px)`,
					px: 1.25,
					py: 0.75,
					borderRadius: 0.75,
					backgroundColor: NEUTRAL[900],
					color: INK.ON_DARK,
					fontSize: FONT_SIZE.CAPTION,
					fontWeight: 600,
					whiteSpace: "nowrap",
					pointerEvents: "none",
					zIndex: 1,
				}}
			>
				{label}
			</Box>
		)}
		{open && (
			// The arrow hangs off the control, not the bubble, so it points at the control however the bubble is aligned.
			<Box
				component="span"
				aria-hidden
				sx={{
					...ENTER_SX,
					position: "absolute",
					bottom: `calc(100% + ${COACH_MARK.OFFSET_PX - 2 * COACH_MARK.ARROW_PX}px)`,
					// Physical: the arrow centres over the control either way round.
					left: "50%",
					marginLeft: `${-COACH_MARK.ARROW_PX}px`,
					border: `${COACH_MARK.ARROW_PX}px solid transparent`,
					borderTopColor: NEUTRAL[900],
					pointerEvents: "none",
					zIndex: 1,
				}}
			/>
		)}
		{children}
	</Box>
);
